export type TSrc = {
  lang: string;
  text: string;
  words?: TSegmentWord[];
};

export type TDst = {
  audio?: string;
  lang: string;
  text: string;
  words?: TSegmentWord[];
};

export type TSegmentWord = {
  id?: string;
  end: string;
  start: string;
  text: string;
};

// A candidate translation for a segment. Mirrors the backend SegmentTranslationVariant /
// ml_api MLApiSegmentTranslationVariant contract.
export type TTranslationVariant = {
  id?: string;
  predicted_duration?: number;
  recommended?: boolean;
  speed_ratio?: number;
  text: string;
};

export const SegmentStatus = {
  Done: 'done',
  Error: 'error',
  Processing: 'processing',
  Updated: 'updated',
} as const;

export type TSegmentStatus = (typeof SegmentStatus)[keyof typeof SegmentStatus];

export const SegmentProcessingStep = {
  Translating: 'translating',
  Voicing: 'voicing',
} as const;

export type TSegmentProcessingStep = (typeof SegmentProcessingStep)[keyof typeof SegmentProcessingStep];

export type TServerSegment = {
  id: string;
  dst: TDst;
  end: string;
  needs_attention?: boolean;
  processing_started_at?: string;
  processing_step?: TSegmentProcessingStep;
  selected_translation_variant?: TTranslationVariant;
  speaker: string;
  src: TSrc;
  start: string;
  status: TSegmentStatus;
  translation_variants?: TTranslationVariant[];
};

export type TExtendedSegment = {
  historyId?: string;
  isEditingSrc?: boolean;
  isMerging?: boolean;
  isRewriting?: boolean;
  isSavingError?: boolean;
  isVoiceCloneError?: boolean;
  overlappedTimestamps?: (TServerSegment['end'] | TServerSegment['start'])[];
  rewritingResult?: string;
} & TServerSegment;

export type TAutoSavingSegmentData = {
  dst?: TExtendedSegment['dst'];
  end?: TExtendedSegment['end'];
  needs_attention?: TExtendedSegment['needs_attention'];
  selected_translation_variant?: TExtendedSegment['selected_translation_variant'];
  speaker?: TExtendedSegment['speaker'];
  start?: TExtendedSegment['start'];
};

export type TUpdatingSegmentData = {
  selected_translation_variant?: null | TExtendedSegment['selected_translation_variant'];
  src?: TExtendedSegment['src'];
  translation_variants?: null | TExtendedSegment['translation_variants'];
} & Omit<TAutoSavingSegmentData, 'selected_translation_variant'> &
  Pick<TExtendedSegment, 'id'>;

export type TEditorHistoryKind = 'add' | 'delete' | 'merge' | 'split' | 'text';

export type TEditorStructuralHistoryKind = Exclude<TEditorHistoryKind, 'text'>;

export type THistorySegment = {
  historyId: string;
} & TServerSegment;

export type TEditorStructuralHistoryChange = {
  after?: THistorySegment;
  afterIndex?: number;
  before?: THistorySegment;
  beforeIndex?: number;
  historyId: string;
};

export type TEditorHistoryEntry =
  | {
      after: TDst | TSrc;
      before: TDst | TSrc;
      field: 'dst' | 'src';
      historyId: string;
      kind: 'text';
    }
  | {
      changes: TEditorStructuralHistoryChange[];
      kind: TEditorStructuralHistoryKind;
    };

export type ParsedSrt = {
  id: string;
  endSeconds: number;
  endTime: string;
  speakerId: null | string;
  startSeconds: number;
  startTime: string;
  text: string;
};

export const SegmentLocation = {
  Bottom: 'bottom',
  Top: 'top',
} as const;

export const MergeSegmentMode = {
  Merge: 'merge',
  MergeAndRetranslate: 'merge-and-retranslate',
} as const;

export type TMergeSegmentMode = (typeof MergeSegmentMode)[keyof typeof MergeSegmentMode];

export type TSplittingSegmentData = {
  dst?: TServerSegment['dst'];
  src?: TServerSegment['src'];
} & Pick<TServerSegment, 'end' | 'start'>;
export type TSegmentLocation = (typeof SegmentLocation)[keyof typeof SegmentLocation];
